import * as React from 'react';
// eslint-disable-next-line no-restricted-imports
import EsriConfig from '@arcgis/core/config';
import {
    EsriCredentialSource,
    EsriTokenWithExpiry,
    register,
    scheduleRefresh,
    unregister,
} from '../esri-identity';
import { logMapError } from '../helpers/utils';
import { useEsriTokenApi } from './use-esri-token-api';
import { useBroadcast } from '../../common/broadcast-channel/use-broadcast';
import { EsriTokenRefreshedMessage } from '../../common/broadcast-channel/types';

// Hosts to register via IdentityManager. Excludes tiles.arcgis.com (unused; tile services are
// scoped by item ID, not a static host) and basemapstyles-api.arcgis.com (IdentityManager
// can't attach a token there reliably — that service takes the token via `apiKey` instead;
// see getEsriToken below).
export const HOSTS = ['https://www.arcgis.com'];

// The SDK's own type declares `apiKey: string`, but its runtime default (see
// @arcgis/core/config.js) is actually `undefined` - this narrows back to that default.
function clearApiKeyFallback(): void {
    EsriConfig.apiKey = undefined as unknown as string;
}

export interface UseEsriIdentityOptions {
    enabled: boolean;
    apiKey: string | undefined;
}

export interface UseEsriIdentityResult {
    /**
     * Department-scoped private webmap to load instead of the default public one, from the
     * most recently resolved token (this window's fetch, or a sibling's broadcast). Undefined
     * unless enabled and the resolved credential is department-scoped.
     */
    webMapPortalId: string | undefined;
    /**
     * True once the identity is settled (flag off, or the initial fetch resolved) — meaning both
     * webMapPortalId and getEsriToken() are final. Wait for this before building the first
     * WebMap or Basemap; a later resolution is missed.
     */
    isEsriIdentityResolved: boolean;
    /** How the most recently resolved credential was scoped - undefined while unresolved or the flag is off. */
    credentialSource: EsriCredentialSource | undefined;
    /** Refreshes the token on a credential failure, falling back to the static key if that fails. Resolves whether a usable credential resulted; never rejects. */
    refreshCredential: () => Promise<boolean>;
    /**
     * Current Esri OAuth token, or undefined if none is valid. Not reactive — call it where a
     * request needs a credential (e.g. Basemap construction), not during render.
     */
    getEsriToken: () => string | undefined;
}

/**
 * Owns the Esri OAuth token lifecycle against the global EsriConfig/IdentityManager
 * singletons: fetches and registers a token, refreshes it proactively and on request
 * failures, and falls back to the legacy static apiKey whenever a fetch/refresh can't
 * produce a usable token. Broadcasts every token this window obtains to sibling CAD
 * Electron windows (each has its own IdentityManager) and applies tokens broadcast by
 * siblings, so windows converge on one credential instead of polling independently.
 * Callers just render this — only `webMapPortalId` needs to reach the component tree, to
 * pick which webmap to load.
 */
export function useEsriIdentity({
    enabled,
    apiKey,
}: UseEsriIdentityOptions): UseEsriIdentityResult {
    const fetchEsriToken = useEsriTokenApi();
    const [webMapPortalId, setWebMapPortalId] = React.useState<string | undefined>(undefined);
    const [credentialSource, setCredentialSource] = React.useState<
        EsriCredentialSource | undefined
    >(undefined);
    // Lazily seeded off the initial `enabled` value: nothing to resolve if starting disabled.
    const [isEsriIdentityResolved, setIsEsriIdentityResolved] = React.useState(!enabled);

    React.useEffect(() => {
        if (!enabled) {
            setIsEsriIdentityResolved(true);
        }
    }, [enabled]);

    // Ref'd so the broadcast handler always reads the latest `enabled` without resubscribing
    // useBroadcast on every flag change.
    const enabledRef = React.useRef(enabled);
    React.useEffect(() => {
        enabledRef.current = enabled;
    }, [enabled]);

    // Dedupes a fetch triggered by scheduleRefresh's timer against one triggered by
    // refreshCredential — a concurrent second caller gets the first's in-flight promise.
    const fetchInFlightRef = React.useRef<Promise<EsriTokenWithExpiry> | null>(null);
    const fetchEsriTokenDeduped = React.useCallback((): Promise<EsriTokenWithExpiry> => {
        if (fetchInFlightRef.current) {
            return fetchInFlightRef.current;
        }
        const promise = fetchEsriToken().finally(() => {
            fetchInFlightRef.current = null;
        });
        fetchInFlightRef.current = promise;
        return promise;
    }, [fetchEsriToken]);

    React.useEffect(() => {
        if (!enabled && apiKey) {
            EsriConfig.apiKey = apiKey;
        }
    }, [enabled, apiKey]);

    // A ref, not state — read synchronously when a request needs it, not via re-render.
    const esriTokenRef = React.useRef<string | undefined>(undefined);
    const getEsriToken = React.useCallback(() => esriTokenRef.current, []);

    // Ref'd to avoid a circular dependency: postMessage comes from useBroadcast, which needs
    // the receipt handler below, which needs to start a refresh, which needs to broadcast.
    const postMessageRef = React.useRef<(message: EsriTokenRefreshedMessage) => void>();
    const registerAndBroadcast = React.useCallback((result: EsriTokenWithExpiry) => {
        register(result.token, result.expiresInSeconds, HOSTS);
        esriTokenRef.current = result.token;
        setWebMapPortalId(result.webMapPortalId);
        setCredentialSource(result.source);
        // Undoes any earlier static-key fallback now that a real OAuth token is registered.
        clearApiKeyFallback();
        postMessageRef.current?.({
            type: 'ESRI_TOKEN_REFRESHED',
            payload: {
                token: result.token,
                expires: Date.now() + result.expiresInSeconds * 1000,
                webMapPortalId: result.webMapPortalId,
                source: result.source,
            },
        });
    }, []);

    // True once torn down (flag off, or unmount); checked before every registerAndBroadcast
    // so a fetch resolving after teardown can't reinstall a stale credential.
    const tornDownRef = React.useRef(false);

    const fetchAndRegister = React.useCallback(() => {
        return fetchEsriTokenDeduped().then((result) => {
            if (!tornDownRef.current) {
                registerAndBroadcast(result);
            }
            return result;
        });
    }, [fetchEsriTokenDeduped, registerAndBroadcast]);

    // Shared by the mount-fetch effect and the broadcast handler, so whichever last obtained
    // a token owns the one active refresh timer instead of both running concurrently.
    const stopRefreshRef = React.useRef<(() => void) | undefined>();
    const startRefresh = React.useCallback(
        (expiresInSeconds: number) => {
            stopRefreshRef.current?.();
            stopRefreshRef.current = scheduleRefresh(
                expiresInSeconds,
                fetchEsriTokenDeduped,
                (refreshed) => registerAndBroadcast(refreshed),
                (error) =>
                    logMapError({
                        source: 'service',
                        event: 'map_auth_refresh_failed',
                        error: error instanceof Error ? error : undefined,
                    }),
                () => {
                    // The token actually expired with no successful refresh — fall back
                    // rather than leave the map unauthenticated.
                    logMapError({
                        source: 'service',
                        event: 'map_auth_token_expired_fallback',
                    });
                    esriTokenRef.current = undefined;
                    if (apiKey) {
                        EsriConfig.apiKey = apiKey;
                    }
                    // No longer registered against a resolved OAuth credential - stale
                    // otherwise, since neither is reset by the retry loop itself.
                    setWebMapPortalId(undefined);
                    setCredentialSource(undefined);
                }
            );
        },
        [fetchEsriTokenDeduped, registerAndBroadcast, apiKey]
    );

    const handleBroadcastToken = React.useCallback(
        (message: EsriTokenRefreshedMessage) => {
            // Ignore a sibling's token if this window's flag is off — it may have just torn
            // its own credentials down, and re-registering here would silently undo that.
            if (!enabledRef.current) {
                return;
            }
            const remainingSeconds = Math.max(0, (message.payload.expires - Date.now()) / 1000);
            // Registers directly (not registerAndBroadcast) — re-broadcasting a token we
            // just received would make sibling windows ping-pong it back and forth forever.
            register(message.payload.token, remainingSeconds, HOSTS);
            esriTokenRef.current = message.payload.token;
            setWebMapPortalId(message.payload.webMapPortalId);
            setCredentialSource(message.payload.source);
            // Undoes any earlier static-key fallback now that a real OAuth token is registered.
            clearApiKeyFallback();
            startRefresh(remainingSeconds);
        },
        [startRefresh]
    );

    const { postMessage } = useBroadcast('esri-token', handleBroadcastToken);
    React.useEffect(() => {
        postMessageRef.current = postMessage;
    }, [postMessage]);

    React.useEffect(() => {
        if (!enabled) {
            return;
        }

        let cancelled = false;

        fetchAndRegister()
            .then((result) => {
                if (cancelled) {
                    return;
                }
                setIsEsriIdentityResolved(true);
                startRefresh(result.expiresInSeconds);
            })
            .catch((error) => {
                logMapError({
                    source: 'service',
                    event: 'map_auth_initial_fetch_failed',
                    error: error instanceof Error ? error : undefined,
                });
                if (cancelled) {
                    return;
                }
                // First-fetch failure falls back to the legacy static key rather than leave
                // the map unauthenticated. A later scheduled-refresh failure skips this path
                // — the already-registered token stays put while scheduleRefresh retries.
                esriTokenRef.current = undefined;
                if (apiKey) {
                    EsriConfig.apiKey = apiKey;
                }
                // The fallback-key path never resolves a department webmap, so stop waiting.
                setIsEsriIdentityResolved(true);
            });

        return () => {
            cancelled = true;
            stopRefreshRef.current?.();
            stopRefreshRef.current = undefined;
        };
    }, [enabled, fetchAndRegister, startRefresh, apiKey]);

    React.useEffect(() => {
        if (!enabled) {
            return;
        }
        tornDownRef.current = false;

        // Keyed only on the flag so callback-identity churn above can't trigger this.
        return () => {
            tornDownRef.current = true;
            unregister();
            esriTokenRef.current = undefined;
            // The torn-down credential's webmap (if any) is no longer valid to keep showing.
            setWebMapPortalId(undefined);
            setCredentialSource(undefined);
        };
    }, [enabled]);

    // Never rejects (so the fire-and-forget interceptor can't see one); the resolved
    // boolean lets retry callers skip a doomed retry.
    const refreshCredential = React.useCallback((): Promise<boolean> => {
        if (!enabled) {
            return Promise.resolve(true);
        }
        return fetchAndRegister()
            .then((result) => {
                if (!tornDownRef.current) {
                    startRefresh(result.expiresInSeconds);
                }
                return true;
            })
            .catch((error) => {
                logMapError({
                    source: 'service',
                    event: 'map_auth_refresh_failed',
                    error: error instanceof Error ? error : undefined,
                });
                // The failure may not be an expiry the SDK's check catches, so drop the
                // stale credential and fall back to the static key rather than leave the
                // map unauthenticated.
                if (!tornDownRef.current && apiKey) {
                    unregister();
                    esriTokenRef.current = undefined;
                    EsriConfig.apiKey = apiKey;
                }
                return !tornDownRef.current && !!apiKey;
            });
    }, [enabled, fetchAndRegister, startRefresh, apiKey]);

    return React.useMemo(
        () => ({
            webMapPortalId,
            isEsriIdentityResolved,
            credentialSource,
            refreshCredential,
            getEsriToken,
        }),
        [webMapPortalId, isEsriIdentityResolved, credentialSource, refreshCredential, getEsriToken]
    );
}
